import React, { useState, useContext, useEffect } from "react";
import { Form, Button, Alert, Card, ProgressBar } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AuthContext from "../contexts/AuthContext";
import api from "../api";

const CSVUpload = () => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState({ text: "", type: "" });
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();

  console.log("Auth context in CSVUpload:", auth);

  // Check direct admin attributes
  const isDirectAdmin =
    auth.user?.is_admin || auth.user?.is_superuser || auth.user?.is_staff;

  // Force admin mode with multiple checks
  const forceAdminAccess =
    auth.isAdmin ||
    isDirectAdmin ||
    (auth.user && (auth.user.is_staff || auth.user.is_superuser));

  console.log("Force admin access:", forceAdminAccess);
  console.log("Direct admin check:", isDirectAdmin);

  // Check if user has permission to upload
  const canUpload = forceAdminAccess || auth.user?.profile?.can_create;

  // Show debug info for admins
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    // Force debug panel for troubleshooting
    if (auth.user && auth.user.username === "admin") {
      console.log("Admin user detected, showing debug panel");
      setShowDebug(true);
    }
  }, [auth.user]);

  // Redirect if user doesn't have upload permissions
  useEffect(() => {
    if (!canUpload && !forceAdminAccess) {
      setMessage({
        text: "You don't have permission to upload files. Please contact an administrator.",
        type: "warning",
      });
    }
  }, [canUpload, forceAdminAccess]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (
        selectedFile.type !== "text/csv" &&
        !selectedFile.name.endsWith(".csv")
      ) {
        setMessage({ text: "Please select a valid CSV file", type: "danger" });
        setFile(null);
        setFileName("");
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setMessage({ text: "", type: "" });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    // Always allow upload for admin users
    if (!canUpload && !forceAdminAccess) {
      setMessage({
        text: "You don't have permission to upload files. Please contact an administrator.",
        type: "danger",
      });
      return;
    }

    if (!file) {
      setMessage({ text: "Please select a CSV file first", type: "warning" });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setUploadProgress(0);

    try {
      await api.post("/api/csv-files/upload_csv/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      setMessage({
        text: "File uploaded and processed successfully!",
        type: "success",
      });
      setFile(null);
      setFileName("");

      // Redirect to data view after successful upload
      setTimeout(() => {
        navigate("/data");
      }, 2000);
    } catch (error) {
      console.error("Upload error:", error);
      if (error.response?.status === 403) {
        setMessage({
          text: "You don't have permission to upload files.",
          type: "danger",
        });
      } else {
        setMessage({
          text:
            error.response?.data?.error ||
            "Error uploading file. Please try again.",
          type: "danger",
        });
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <h2 className="mb-4">Upload CSV File</h2>

      {message.text && (
        <Alert
          variant={message.type}
          dismissible
          onClose={() => setMessage({ text: "", type: "" })}
        >
          {message.text}
        </Alert>
      )}

      {showDebug && (
        <Alert variant="info" className="mb-3">
          <strong>Admin Debug Info:</strong>
          <p>User: {auth.user?.username}</p>
          <p>Auth isAdmin: {auth.isAdmin ? "Yes" : "No"}</p>
          <p>User is_admin: {auth.user?.is_admin ? "Yes" : "No"}</p>
          <p>Is Superuser: {auth.user?.is_superuser ? "Yes" : "No"}</p>
          <p>Is Staff: {auth.user?.is_staff ? "Yes" : "No"}</p>
          <p>Force Admin Access: {forceAdminAccess ? "Yes" : "No"}</p>
          <p>Direct Admin Check: {isDirectAdmin ? "Yes" : "No"}</p>
          <p>Can Upload: {canUpload ? "Yes" : "No"}</p>
          <p>
            Profile Can Create: {auth.user?.profile?.can_create ? "Yes" : "No"}
          </p>
          <p>Profile Can Edit: {auth.user?.profile?.can_edit ? "Yes" : "No"}</p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowDebug(false)}
          >
            Hide Debug Info
          </Button>
        </Alert>
      )}

      {!canUpload && !forceAdminAccess ? (
        <Card className="text-center p-4">
          <Card.Body>
            <Card.Title>Permission Denied</Card.Title>
            <Card.Text>
              You don't have permission to upload files. Please contact an
              administrator if you need this access.
            </Card.Text>
            <Button variant="primary" onClick={() => navigate("/data")}>
              View Data
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          {!showDebug && (
            <Button
              variant="outline-info"
              size="sm"
              className="mb-3"
              onClick={() => setShowDebug(true)}
            >
              Show Admin Debug Info
            </Button>
          )}

          <Card className="csv-form">
            <Card.Body>
              <Form onSubmit={handleUpload}>
                <Form.Group controlId="csvFile" className="mb-3">
                  <Form.Label>Select CSV File</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    accept=".csv"
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Only CSV files are supported.
                  </Form.Text>
                </Form.Group>

                {fileName && (
                  <div className="mb-3">
                    <p className="mb-1">
                      <strong>Selected file:</strong> {fileName}
                    </p>
                  </div>
                )}

                {uploading && (
                  <div className="mb-3">
                    <ProgressBar
                      now={uploadProgress}
                      label={`${uploadProgress}%`}
                    />
                    <p className="text-center mt-2">
                      Uploading and processing...
                    </p>
                  </div>
                )}

                <Button
                  variant="primary"
                  type="submit"
                  disabled={!file || uploading}
                >
                  {uploading ? "Uploading..." : "Upload CSV"}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <div className="mt-4">
            <h4>Instructions</h4>
            <ul>
              <li>The CSV file should have a header row with column names</li>
              <li>
                After uploading, you can view the imported data in the "View
                Data" section
              </li>
              <li>Large files may take longer to process</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default CSVUpload;
